/**
 * URL Parameter Builder
 * Visual builder for configuring URL parameters with dropdown selections
 */

import React, { useState } from "react";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  type URLParameters,
  type URLParameterDefinition,
  PARAMETER_SOURCES,
  GENERATION_FORMATS,
} from "../../types/voucherTemplate.types";

interface Props {
  urlTemplate: string;
  parameters: URLParameters;
  onChange: (urlTemplate: string, parameters: URLParameters) => void;
}

const URLParameterBuilder: React.FC<Props> = ({
  urlTemplate,
  parameters,
  onChange,
}) => {
  const [newParamName, setNewParamName] = useState("");

  const handleAddParameter = () => {
    if (!newParamName.trim()) {
      return;
    }

    const paramName = newParamName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, "_");

    const newParams = {
      ...parameters,
      [paramName]: {
        type: "static",
        label: newParamName.trim(),
        value: "",
      } as URLParameterDefinition,
    };

    onChange(urlTemplate, newParams);
    setNewParamName("");
  };

  const handleRemoveParameter = (paramName: string) => {
    const newParams = { ...parameters };
    delete newParams[paramName];

    // Remove from template
    const newTemplate = urlTemplate.replace(
      new RegExp(`[&?]${paramName}={${paramName}}`, "g"),
      "",
    );

    onChange(newTemplate, newParams);
  };

  const handleParameterChange = (
    paramName: string,
    field: keyof URLParameterDefinition,
    value: unknown,
  ) => {
    const param = parameters[paramName];
    const updatedParam = { ...param, [field]: value } as URLParameterDefinition;

    // Reset dependent fields when type changes
    if (field === "type") {
      if (value === "static") {
        delete updatedParam.source;
        delete updatedParam.format;
        updatedParam.value = "";
      } else if (value === "mapped") {
        delete updatedParam.value;
        delete updatedParam.format;
        updatedParam.source = "";
      } else if (value === "generated") {
        delete updatedParam.value;
        delete updatedParam.source;
        updatedParam.format = "alphanumeric_8";
      }
    }

    onChange(urlTemplate, {
      ...parameters,
      [paramName]: updatedParam,
    });
  };

  const handleTemplateChange = (value: string) => {
    onChange(value, parameters);
  };

  const insertParameterIntoTemplate = (paramName: string) => {
    // Add parameter to template if not already present
    if (!urlTemplate.includes(`{${paramName}}`)) {
      const separator = urlTemplate.includes("?") ? "&" : "?";
      const newTemplate = `${urlTemplate}${separator}${paramName}={${paramName}}`;
      onChange(newTemplate, parameters);
    }
  };

  return (
    <div className="space-y-4">
      {/* URL Template */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          URL Template
        </label>
        <input
          type="text"
          value={urlTemplate}
          onChange={(e) => handleTemplateChange(e.target.value)}
          placeholder="https://example.com/signup?promo={promo_code}&ref={affiliate_id}"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        />
        <p className="mt-1 text-xs text-gray-500">
          Use {"{parameter_name}"} placeholders for dynamic values
        </p>
      </div>

      {/* Parameter List */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          URL Parameters
        </label>

        <div className="space-y-3">
          {Object.entries(parameters).map(([paramName, param]) => (
            <div
              key={paramName}
              className="border border-gray-200 rounded-lg p-4 bg-gray-50"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="font-medium text-sm text-gray-900">
                    {paramName}
                  </div>
                  <div className="text-xs text-gray-500">{param.label}</div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveParameter(paramName)}
                  className="text-red-600 hover:text-red-700 ml-2"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Parameter Type */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    value={param.type}
                    onChange={(e) =>
                      handleParameterChange(paramName, "type", e.target.value)
                    }
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="static">Static Value</option>
                    <option value="mapped">Mapped from Data</option>
                    <option value="generated">Auto-Generated</option>
                  </select>
                </div>

                {/* Type-specific fields */}
                {param.type === "static" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Value
                    </label>
                    <input
                      type="text"
                      value={param.value || ""}
                      onChange={(e) =>
                        handleParameterChange(
                          paramName,
                          "value",
                          e.target.value,
                        )
                      }
                      placeholder="Enter static value"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                )}

                {param.type === "mapped" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Map From
                    </label>
                    <select
                      value={param.source || ""}
                      onChange={(e) =>
                        handleParameterChange(
                          paramName,
                          "source",
                          e.target.value,
                        )
                      }
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select source...</option>
                      {/* PARAMETER_SOURCES is grouped by entity ({ organization: [...], ... }),
                          not an array: calling .map on it threw and crashed the form. */}
                      {Object.entries(PARAMETER_SOURCES).map(
                        ([group, sources]) => (
                          <optgroup key={group} label={group}>
                            {sources.map((source) => (
                              <option key={source.value} value={source.value}>
                                {source.label}
                              </option>
                            ))}
                          </optgroup>
                        ),
                      )}
                    </select>
                  </div>
                )}

                {param.type === "generated" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Format
                    </label>
                    <select
                      value={param.format || "alphanumeric_8"}
                      onChange={(e) =>
                        handleParameterChange(
                          paramName,
                          "format",
                          e.target.value,
                        )
                      }
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      {GENERATION_FORMATS.map((format) => (
                        <option key={format.value} value={format.value}>
                          {format.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => insertParameterIntoTemplate(paramName)}
                className="mt-2 text-xs text-primary-600 hover:text-primary-700"
              >
                Insert into template
              </button>
            </div>
          ))}
        </div>

        {/* Add Parameter */}
        <div className="mt-3 flex gap-2">
          <input
            type="text"
            value={newParamName}
            onChange={(e) => setNewParamName(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleAddParameter()}
            placeholder="Parameter name (e.g., affiliate_id)"
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          />
          <button
            type="button"
            onClick={handleAddParameter}
            className="px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 flex items-center gap-1"
          >
            <PlusIcon className="h-4 w-4" />
            Add Parameter
          </button>
        </div>
      </div>
    </div>
  );
};

export default URLParameterBuilder;
